import { create } from 'zustand'
import { getSession } from '@/auth/auth.service'
import { logoutUsuario } from '@/common/utils/services/contratos.service'
import {
  setSessionCookies,
  clearSessionCookies,
  getUserToken,
  getIdCertificado,
  msUntilJwtExp,
} from '@/auth/auth.utils'
import { captureLoginBounceEvent, maskToken } from '@/auth/auth.sentry'
import { buildPhpLogoutUrl } from '@/common/utils/navigation/welcome-url.js'

const MAX_RETRY_ATTEMPTS = 3
const RETRY_DELAY_MS = 2000

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const getAuthFailureDetails = (res) => {
  if (res.code === 401 || res.code === 403) {
    return {
      level: 'info',
      reason: 'Token validation failed - expected bounce',
    }
  }

  if (res.code === 0) {
    return {
      level: 'error',
      reason: 'Network timeout during authentication',
    }
  }

  if (res.code >= 500) {
    return {
      level: 'error',
      reason: 'Server error during authentication',
    }
  }

  return {
    level: 'warning',
    reason: 'Authentication validation failed',
  }
}

const captureUnavailableTokenBounce = () => {
  captureLoginBounceEvent({
    tokenSent: 'N/A',
    response: {
      status: 401,
      payload: { message: 'No token available' },
    },
    authValid: false,
    level: 'info',
    reason: 'No token available for authentication',
  })
}

const captureInvalidSessionBounce = ({ sid, res }) => {
  const { level, reason } = getAuthFailureDetails(res)

  captureLoginBounceEvent({
    tokenSent: maskToken(sid),
    response: {
      status: res.code,
      payload: {
        errorCode: res.errorCode,
        message: res.message,
        ...res.payload,
      },
    },
    authValid: res.authValid || false,
    level,
    reason,
  })
}

const captureRetryFailureBounce = ({ sid, lastError }) => {
  captureLoginBounceEvent({
    tokenSent: maskToken(sid),
    response: {
      status: lastError?.response?.status || 0,
      payload: lastError?.response?.data || { message: lastError?.message || 'Unknown error' },
    },
    authValid: false,
    level: 'error',
    reason: `Authentication failed after ${MAX_RETRY_ATTEMPTS} attempts - technical failure`,
  })
}

const captureUncaughtAuthException = ({ uuid, error }) => {
  captureLoginBounceEvent({
    tokenSent: maskToken(uuid || getUserToken()),
    response: {
      status: 0,
      payload: { message: error?.message || 'Uncaught exception' },
    },
    authValid: false,
    level: 'error',
    reason: 'Uncaught exception during authentication',
  })
}

const buildSessionState = ({ resData, certId }) => {
  const { accessToken, refreshToken, userToken, expiresIn, refreshExpiresIn, contrato, idUsuario, emailUsuario } =
    resData

  return {
    accessToken,
    refreshToken,
    userToken,
    expiresIn,
    refreshExpiresIn,
    sessionCookies: {
      accessToken,
      refreshToken,
      userToken,
      contrato,
      idCertificado: certId,
      idUsuario,
      emailUsuario,
      maxAgeAccessSec: expiresIn,
      maxAgeRefreshSec: refreshExpiresIn,
    },
    storeState: {
      token: accessToken,
      userToken,
      idCertificado: certId,
      contrato,
      idUsuario,
      emailUsuario,
    },
  }
}

const getRefreshDelay = ({ accessToken, expiresIn }) => {
  const ms = msUntilJwtExp(accessToken, expiresIn)
  const MIN_WAIT = 60_000
  const REFRESH_MARGIN = 45_000

  return Math.max(MIN_WAIT, ms - REFRESH_MARGIN)
}

export const useAuthStore = create((set, get) => ({
  token: null,
  userToken: null,
  refreshTimer: null,
  idCertificado: null,
  contrato: null,
  idUsuario: null,
  emailUsuario: null,
  setIdCertificado: (id) => set({ idCertificado: id }),
  _authInFlight: false,
  _authPromise: null,

  cleanup: () => {
    const t = get().refreshTimer
    if (t) {
      window.clearTimeout(t)
      set({ refreshTimer: null })
    }
  },

  logout: async () => {
    const t = get().refreshTimer
    if (t) window.clearTimeout(t)

    // Obtener datos para el logout
    const userToken = get().userToken
    const emailUsuario = get().emailUsuario

    // Llamar al endpoint de logout para revocar el token en Keycloak
    if (userToken && emailUsuario) {
      try {
        await logoutUsuario(userToken, emailUsuario)
      } catch (error) {
        // Continuar con el logout local incluso si falla el backend
        console.error('Error al llamar al endpoint de logout:', error)
      }
    }

    clearSessionCookies()
    window.location.replace(buildPhpLogoutUrl(import.meta.env.VITE_PHP_REDIRECT))
  },

  checkAuth: ({ uuid, idCertificado, skipCertValidation = false } = {}) => {
    const { _authPromise } = get()
    if (_authPromise) return _authPromise

    const p = (async () => {
      set({ _authInFlight: true })

      const sid = uuid || getUserToken()
      const certId = idCertificado || getIdCertificado() || get().idCertificado

      if (!sid) {
        captureUnavailableTokenBounce()
        get().logout()
        return false
      }

      let lastError = null
      for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
        try {
          const res = await getSession({ uuid: sid, idCertificado: certId, skipCertValidation })

          if (res.code !== 200 || !res.data?.accessToken) {
            captureInvalidSessionBounce({ sid, res })
            get().logout()
            return false
          }

          const sessionState = buildSessionState({ resData: res.data, certId })

          setSessionCookies(sessionState.sessionCookies)
          set(sessionState.storeState)

          const old = get().refreshTimer
          if (old) window.clearTimeout(old)

          const timer = window.setTimeout(async () => {
            try {
              const currentState = get()
              const ok = await get().checkAuth({
                uuid: currentState.userToken || sid,
                idCertificado: currentState.idCertificado || certId,
              })
              if (!ok) {
                get().logout()
              }
            } catch {
              get().logout()
            }
          }, getRefreshDelay(sessionState))

          set({ refreshTimer: timer })
          return true
        } catch (error) {
          lastError = error

          if (attempt < MAX_RETRY_ATTEMPTS - 1) {
            await wait(RETRY_DELAY_MS)
          }
        }
      }

      captureRetryFailureBounce({ sid, lastError })
      get().logout()
      return false
    })()
      .catch((err) => {
        captureUncaughtAuthException({ uuid, error: err })
        get().logout()
        return false
      })
      .finally(() => {
        set({ _authInFlight: false, _authPromise: null })
      })

    set({ _authPromise: p })
    return p
  },
}))
